import { strToU8, unzip, zip, type AsyncZippable } from 'fflate';
import type { MaipoPackageManifestV01, MaipoPublicSceneV01, PublicAsset } from './types';
import { serializeMaipoPublicScene } from './canonical';
import { parseMaipoPackageManifest, parseMaipoPublicScene, verifyPublicAssets } from './validation';
import { decodeMaipoJsonBytes, MaipoJsonDecodeError } from './json';

const PACKAGE_SCHEMA = 'https://maipo.design/schema/0.1/package.schema.json' as const;
const MANIFEST_ENTRY = 'manifest.json';
const SCENE_ENTRY = 'scene.maipo.json' as const;
const MAX_ARCHIVE_BYTES = 1024 * 1024 * 1024;
const MAX_EXPANDED_BYTES = 1024 * 1024 * 1024;
const MAX_ENTRIES = 4098;
const MAX_MANIFEST_BYTES = 2 * 1024 * 1024;
const MAX_SCENE_BYTES = 64 * 1024 * 1024;
const ZIP_TIMESTAMP = new Date('1980-01-01T00:00:00.000Z');

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('The package operation was cancelled.', 'AbortError');
}

function safePath(path: string): boolean {
  return path.length > 0 && !path.startsWith('/') && !path.includes('\\') && !path.includes('\0') && !path.split('/').includes('..');
}

function preflightArchive(bytes: Uint8Array): void {
  if (bytes.byteLength > MAX_ARCHIVE_BYTES) throw new Error('Maipo public package exceeds the 1 GiB archive limit.');
  if (bytes.byteLength < 22) throw new Error('Maipo public package has no valid ZIP directory.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const minimum = Math.max(0, bytes.byteLength - 65_557);
  let eocd = -1;
  for (let offset = bytes.byteLength - 22; offset >= minimum; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) { eocd = offset; break; }
  }
  if (eocd < 0) throw new Error('Maipo public package has no valid ZIP directory.');
  const count = view.getUint16(eocd + 10, true);
  const centralSize = view.getUint32(eocd + 12, true);
  const centralOffset = view.getUint32(eocd + 16, true);
  if (count === 0xffff || centralOffset === 0xffffffff || centralSize === 0xffffffff) throw new Error('ZIP64 packages are not supported by Maipo public package 0.1.');
  if (count < 2 || count > MAX_ENTRIES) throw new Error('Maipo public package entry count is outside the safety limit.');
  if (centralOffset + centralSize > eocd) throw new Error('Maipo public package ZIP directory is malformed.');
  const paths = new Set<string>();
  const decoder = new TextDecoder('utf-8', { fatal: true });
  let offset = centralOffset;
  let expanded = 0;
  for (let index = 0; index < count; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== 0x02014b50) throw new Error('Maipo public package ZIP directory is malformed.');
    const flags = view.getUint16(offset + 8, true);
    const method = view.getUint16(offset + 10, true);
    const uncompressed = view.getUint32(offset + 24, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    if ((flags & 1) !== 0) throw new Error('Encrypted ZIP entries are not allowed in a Maipo public package.');
    if (method !== 0 && method !== 8) throw new Error(`Unsupported ZIP compression method ${String(method)}.`);
    const nameStart = offset + 46;
    const nameEnd = nameStart + nameLength;
    if (nameEnd > bytes.byteLength) throw new Error('Maipo public package entry name is truncated.');
    let path: string;
    try { path = decoder.decode(bytes.subarray(nameStart, nameEnd)); }
    catch { throw new Error('Maipo public package contains a non-UTF-8 path.'); }
    if (!safePath(path) || paths.has(path)) throw new Error(`Unsafe or duplicate package path: ${path || '(empty)'}.`);
    paths.add(path);
    expanded += uncompressed;
    if (!Number.isSafeInteger(expanded) || expanded > MAX_EXPANDED_BYTES) throw new Error('Maipo public package exceeds the 1 GiB expanded-data limit.');
    offset = nameEnd + extraLength + commentLength;
  }
  if (offset !== centralOffset + centralSize) throw new Error('Maipo public package ZIP directory length is inconsistent.');
}

function zipAsync(entries: AsyncZippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => zip(entries, { level: 6, mtime: ZIP_TIMESTAMP, os: 3 }, (error, result) => error ? reject(error) : resolve(result)));
}

function unzipAsync(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => unzip(bytes, (error, result) => error ? reject(error) : resolve(result)));
}

function assetBytes(asset: PublicAsset): Uint8Array {
  try {
    const binary = atob(asset.data);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new Error(`Asset ${asset.id} is not valid base64.`);
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  const size = 32_766;
  let result = '';
  for (let offset = 0; offset < bytes.length; offset += size) {
    let binary = '';
    for (const byte of bytes.subarray(offset, offset + size)) binary += String.fromCharCode(byte);
    result += btoa(binary);
  }
  return result;
}

async function digest(bytes: Uint8Array): Promise<string> {
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return `sha256:${[...hash].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
  return left.byteLength === right.byteLength && left.every((byte, index) => byte === right[index]);
}

function parseJson(entry: Uint8Array | undefined, name: string, limit: number): unknown {
  if (!entry) throw new Error(`Maipo public package is missing ${name}.`);
  if (entry.byteLength > limit) throw new Error(`${name} exceeds its package safety limit.`);
  try {
    return decodeMaipoJsonBytes(entry, { maxBytes: limit, maxCodeUnits: limit });
  } catch (error) {
    const detail = error instanceof MaipoJsonDecodeError ? ` (${error.code} at line ${String(error.line)}, column ${String(error.column)})` : '';
    throw new Error(`${name} contains invalid JSON${detail}.`, { cause: error });
  }
}

export function serializeMaipoPackageManifest(value: unknown): string {
  const manifest = parseMaipoPackageManifest(value);
  const canonical: MaipoPackageManifestV01 = {
    $schema: PACKAGE_SCHEMA,
    format: manifest.format,
    version: manifest.version,
    scene: manifest.scene,
    sceneDigest: manifest.sceneDigest,
    entryCount: manifest.entryCount,
    expandedBytes: manifest.expandedBytes,
    assets: manifest.assets.map((asset) => ({
      id: asset.id,
      digest: asset.digest,
      mediaType: asset.mediaType,
      byteLength: asset.byteLength,
      path: asset.path,
    })),
  };
  return `${JSON.stringify(canonical, null, 2)}\n`;
}

/** Packs canonical public JSON and deduplicated content-addressed assets. */
export async function packMaipoPublicScene(value: unknown, signal?: AbortSignal): Promise<Uint8Array> {
  throwIfAborted(signal);
  const scene = structuredClone(parseMaipoPublicScene(value));
  const assetDiagnostics = await verifyPublicAssets(scene);
  const errors = assetDiagnostics.filter((entry) => entry.severity === 'error');
  if (errors.length) throw new Error(`Cannot pack invalid assets: ${errors.map((entry) => `${entry.code}:${entry.target ?? ''}`).join(', ')}.`);

  const packedScene = structuredClone(scene);
  packedScene.assets.forEach((asset) => { asset.data = ''; });
  const sceneBytes = strToU8(serializeMaipoPublicScene(packedScene));
  if (sceneBytes.byteLength > MAX_SCENE_BYTES) throw new Error('Canonical scene JSON exceeds the 64 MiB package limit.');
  const uniqueAssets = new Map<string, Uint8Array>();
  const manifestAssets = scene.assets.map((asset) => {
    const path = `assets/sha256/${asset.digest.slice('sha256:'.length)}`;
    uniqueAssets.set(path, assetBytes(asset));
    return { id: asset.id, digest: asset.digest, mediaType: asset.mediaType, byteLength: asset.byteLength, path };
  });
  const expandedBytes = sceneBytes.byteLength + [...uniqueAssets.values()].reduce((sum, bytes) => sum + bytes.byteLength, 0);
  if (expandedBytes > MAX_EXPANDED_BYTES) throw new Error('Maipo public package exceeds the 1 GiB expanded-data limit.');
  const manifest: MaipoPackageManifestV01 = {
    $schema: PACKAGE_SCHEMA,
    format: 'maipo-package',
    version: '0.1',
    scene: SCENE_ENTRY,
    sceneDigest: await digest(sceneBytes),
    entryCount: uniqueAssets.size + 2,
    expandedBytes,
    assets: manifestAssets,
  };
  const options = { level: 6 as const, mtime: ZIP_TIMESTAMP, os: 3, attrs: 0o100644 << 16 };
  const entries: AsyncZippable = {
    [MANIFEST_ENTRY]: [strToU8(serializeMaipoPackageManifest(manifest)), options],
    [SCENE_ENTRY]: [sceneBytes, options],
  };
  for (const path of [...uniqueAssets.keys()].sort()) entries[path] = [uniqueAssets.get(path)!, options];
  throwIfAborted(signal);
  return zipAsync(entries);
}

/** Materializes the packed projection back into the canonical public scene. */
export async function unpackMaipoPublicScene(bytes: Uint8Array, signal?: AbortSignal): Promise<MaipoPublicSceneV01> {
  throwIfAborted(signal);
  preflightArchive(bytes);
  const entries = await unzipAsync(bytes);
  throwIfAborted(signal);
  const manifestBytes = entries[MANIFEST_ENTRY];
  const manifest = parseMaipoPackageManifest(parseJson(manifestBytes, MANIFEST_ENTRY, MAX_MANIFEST_BYTES));
  if (!manifestBytes || !sameBytes(manifestBytes, strToU8(serializeMaipoPackageManifest(manifest)))) throw new Error('manifest.json is not in canonical Maipo package form.');
  if (manifest.entryCount !== Object.keys(entries).length) throw new Error('Maipo package entry count does not match its manifest.');
  const sceneBytes = entries[manifest.scene];
  if (!sceneBytes) throw new Error(`Maipo public package is missing ${manifest.scene}.`);
  if (await digest(sceneBytes) !== manifest.sceneDigest) throw new Error('Packed scene JSON does not match its declared digest.');
  const packedScene = parseMaipoPublicScene(parseJson(sceneBytes, manifest.scene, MAX_SCENE_BYTES));
  if (!sameBytes(sceneBytes, strToU8(serializeMaipoPublicScene(packedScene)))) throw new Error('Packed scene JSON is not canonical.');
  if (packedScene.assets.length !== manifest.assets.length) throw new Error('Packed scene assets do not match the package manifest.');

  const expectedEntries = new Set<string>([MANIFEST_ENTRY, manifest.scene]);
  const materialized = structuredClone(packedScene);
  for (const [index, item] of manifest.assets.entries()) {
    const asset = materialized.assets[index];
    if (!asset || asset.id !== item.id || asset.digest !== item.digest || asset.mediaType !== item.mediaType || asset.byteLength !== item.byteLength || asset.data !== '') throw new Error(`Packed asset metadata does not match manifest item ${item.id}.`);
    expectedEntries.add(item.path);
    const content = entries[item.path];
    if (!content || content.byteLength !== item.byteLength || await digest(content) !== item.digest) throw new Error(`Packed asset ${item.id} is missing or corrupt.`);
    asset.data = bytesToBase64(content);
  }
  const extras = Object.keys(entries).filter((path) => !expectedEntries.has(path));
  if (extras.length) throw new Error(`Maipo public package contains unreferenced entries: ${extras.join(', ')}.`);
  const expanded = sceneBytes.byteLength + [...new Set(manifest.assets.map((asset) => asset.path))].reduce((sum, path) => sum + (entries[path]?.byteLength ?? 0), 0);
  if (expanded !== manifest.expandedBytes) throw new Error('Maipo package expanded byte count does not match its manifest.');
  const diagnostics = await verifyPublicAssets(materialized);
  const errors = diagnostics.filter((entry) => entry.severity === 'error');
  if (errors.length) throw new Error(`Materialized package assets failed verification: ${errors.map((entry) => entry.code).join(', ')}.`);
  return parseMaipoPublicScene(materialized);
}
